import { createSlice } from '@reduxjs/toolkit';
import { IHolidayState } from '../../interface/holiday.interface';
import {
  fetchHolidays,
  fetchHolidayById,
  createHoliday,
  updateHoliday,
  deleteHoliday,
} from '../action/holiday.action';

const initialState: IHolidayState = {
  holidays: [],
  selectedHoliday: null,
  isLoading: false,
  isDetailLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  total: 0,
  error: undefined,
  formError: undefined,
  deleteError: undefined,
};

const holidaySlice = createSlice({
  name: 'holiday',
  initialState,
  reducers: {
    resetHoliday(state) {
      state.holidays = [];
      state.selectedHoliday = null;
      state.total = 0;
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHolidays.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(fetchHolidays.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = undefined;
        state.holidays = action.payload.data.holidays;
        state.total = action.payload.data.total;
      })
      .addCase(fetchHolidays.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to fetch holidays.';
      })
      .addCase(fetchHolidayById.pending, (state) => {
        state.isDetailLoading = true;
        state.error = undefined;
      })
      .addCase(fetchHolidayById.fulfilled, (state, action) => {
        state.isDetailLoading = false;
        state.error = undefined;
        state.selectedHoliday = action.payload.data;
      })
      .addCase(fetchHolidayById.rejected, (state, action) => {
        state.isDetailLoading = false;
        state.error = action.payload?.message || 'Failed to fetch holiday details.';
      })
      .addCase(createHoliday.pending, (state) => {
        state.isCreating = true;
        state.formError = undefined;
      })
      .addCase(createHoliday.fulfilled, (state) => {
        // Backend only returns { holiday: { id, name } } on create, not a full
        // list item (no dateStatus/daysCount/canEdit* etc.) - the caller
        // re-fetches the list on success instead of merging this in.
        state.isCreating = false;
        state.formError = undefined;
      })
      .addCase(createHoliday.rejected, (state, action) => {
        state.isCreating = false;
        state.formError = action.payload?.message || 'Failed to create holiday.';
      })
      .addCase(updateHoliday.pending, (state) => {
        state.isUpdating = true;
        state.formError = undefined;
      })
      .addCase(updateHoliday.fulfilled, (state) => {
        // Same partial-response shape as create - caller re-fetches the list
        // and re-fetches selectedHoliday by id on success.
        state.isUpdating = false;
        state.formError = undefined;
      })
      .addCase(updateHoliday.rejected, (state, action) => {
        state.isUpdating = false;
        state.formError = action.payload?.message || 'Failed to update holiday.';
      })
      .addCase(deleteHoliday.pending, (state) => {
        state.isDeleting = true;
        state.deleteError = undefined;
      })
      .addCase(deleteHoliday.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.deleteError = undefined;
        state.holidays = state.holidays.filter((h) => h.id !== action.payload.id);
        state.total -= 1;
      })
      .addCase(deleteHoliday.rejected, (state, action) => {
        state.isDeleting = false;
        state.deleteError = action.payload?.message || 'Failed to delete holiday.';
      });
  },
});

export const { resetHoliday } = holidaySlice.actions;
export default holidaySlice.reducer;
